import { Box, Tooltip, Typography } from "@mui/material";
import { alpha, Breakpoint, lighten, Theme } from "@mui/material/styles";
import { SystemStyleObject } from "@mui/system";
import { t } from "i18next";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import { clamp } from "../../../shared/chartMath";
import { Sx, TABULAR_NUMERALS } from "./shared";
import { sharedScale } from "./sharedScale";
import { sharesOf } from "./sharesOf";

/* =======================================================================
   Breakdown — composition, not a list of totals
   ======================================================================= */

interface BreakdownItem {
  label: string;
  value: number;
  /** What of the value the row's bar sets off, where the bars are split. */
  part?: number;
  /** What of the value the bar sets off at its end, where the split has one. */
  endPart?: number;
}

/**
 * Splits every bar in two: the row's part from the start of the bar in one
 * colour, the rest of its value in another. The bars keep their widths, so
 * the rows still compare by value. The label names the part on hover. With an
 * `end`, a third part closes the bar, unfilled within a dashed border.
 */
interface BreakdownSplit {
  label: string;
  color: string;
  restColor: string;
  end?: { label: string; color: string };
}

/** Minimum bar width so a real value never renders as an empty track. */
const MIN_BAR_PCT = 1.5;

/** How far the zero line stands out above and below the thin track, in px. */
const ZERO_LINE_OVERHANG_PX = 2;

/**
 * The ramp runs from the primary colour lifted by RAMP_LIGHTEN_START to lifted
 * by RAMP_LIGHTEN_START + RAMP_LIGHTEN_SPAN, in at most RAMP_STEPS steps: past
 * that rank every row shares the lightest shade, so a long tail stays legible
 * instead of fading to white.
 */
const RAMP_LIGHTEN_START = 0.15;
const RAMP_LIGHTEN_SPAN = 0.6;
const RAMP_STEPS = 3;

/**
 * The widths of the columns, in theme.spacing units: of the amounts as they
 * are rounded and as they are written in full, of the shares, and of the
 * labels where they stand beside their bars.
 */
const ROUNDED_VALUE_WIDTH = 13.5;
const EXACT_VALUE_WIDTH = 16;
const SHARE_WIDTH = 6;
const LABEL_WIDTH = 24;
/** The least a bar keeps of a line it shares with its label. */
const MIN_BAR_WIDTH = 15;

/** The rule between two lines is fainter than the one over the total. */
const LINE_RULE_ALPHA = 0.06;

/** Rank 0 is the primary colour lightly lifted; the tail fades towards it. */
const rampColor = (theme: Theme, rank: number, count: number) =>
  lighten(
    theme.palette.primary.main,
    RAMP_LIGHTEN_START +
      (count > 1
        ? Math.min(rank, RAMP_STEPS) / Math.min(count - 1, RAMP_STEPS)
        : 0) *
        RAMP_LIGHTEN_SPAN,
  );

export const Breakdown = ({
  items,
  split,
  exact = false,
  lineFrom,
  sx,
}: {
  items: BreakdownItem[];
  split?: BreakdownSplit;
  /**
   * Writes the amounts in full, where the list is what states them. Otherwise
   * they are rounded, and shown in full on hover.
   */
  exact?: boolean;
  /**
   * From this breakpoint up a row is one line, its label beside its bar, with
   * a rule between the lines: for a list that has the width of a page there.
   * Below it, and without it, the label stands over its bar.
   */
  lineFrom?: Breakpoint;
  sx?: Sx;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();

  const sorted = [...items].sort((a, b) => b.value - a.value);
  const values = sorted.map((r) => r.value);
  const total = values.reduce((sum, v) => sum + v, 0);
  // a row below zero among rows above it is an offset, not a part: its bar
  // reaches left from a shared zero line and it has no share of the whole
  const { placement } = sharedScale(values, MIN_BAR_PCT);
  const shares = sharesOf(values);

  const format = exact ? currency.format : currency.formatCompact;
  const figures = `${theme.spacing(
    exact ? EXACT_VALUE_WIDTH : ROUNDED_VALUE_WIDTH,
  )} ${theme.spacing(SHARE_WIDTH)}`;
  // what changes where a row is one line
  const inLine = (
    styles: SystemStyleObject<Theme>,
  ): SystemStyleObject<Theme> =>
    lineFrom ? { [theme.breakpoints.up(lineFrom)]: styles } : {};

  const grid = {
    display: "grid",
    gridTemplateColumns: `minmax(0, 1fr) ${figures}`,
    gap: theme.spacing(1.5),
  } as const;
  // the columns of a row that is one line: the label has one of its own
  const lineGrid = {
    gridTemplateColumns: `${theme.spacing(LABEL_WIDTH)} minmax(${theme.spacing(
      MIN_BAR_WIDTH,
    )}, 1fr) ${figures}`,
    columnGap: theme.spacing(2),
  } as const;

  return (
    <Box sx={sx}>
      {sorted.map((r, rank) => {
        const share = shares[rank];
        const { pct, start, zeroPct } = placement(r.value);
        const amount = (
          <Typography
            variant="body2"
            align="right"
            noWrap
            sx={{
              ...TABULAR_NUMERALS,
              fontWeight: 700,
              cursor: exact ? undefined : "default",
            }}
          >
            {format(r.value)}
          </Typography>
        );
        return (
          <Box
            key={r.label}
            sx={{
              ...grid,
              alignItems: "center",
              py: 0.9,
              ...inLine({
                ...lineGrid,
                py: 1.125,
                borderTop: `1px solid ${alpha(
                  theme.palette.divider,
                  LINE_RULE_ALPHA,
                )}`,
              }),
            }}
          >
            {/* one cell where the label stands over its bar; where the two
                share a line, each is a cell of its own */}
            <Box sx={inLine({ display: "contents" })}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  mb: 0.5,
                  ...inLine({ fontWeight: 600, mb: 0 }),
                }}
              >
                {r.label}
              </Typography>
              {/* the track clips its bar; the zero line stands out past the
                  track, so it sits beside it rather than inside */}
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    position: "relative",
                    height: theme.spacing(0.75),
                    bgcolor: theme.palette.action.hover,
                    borderRadius: 3,
                    overflow: "hidden",
                    ...inLine({ height: theme.spacing(1) }),
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: `${start}%`,
                      width: `${pct}%`,
                      borderRadius: 3,
                      overflow: "hidden",
                      // split bars say what a part is, so they leave the
                      // ramp, which only tells the rows apart
                      bgcolor: split
                        ? split.restColor
                        : rampColor(theme, rank, sorted.length),
                    }}
                  >
                    {split && r.part !== undefined && r.value !== 0 && (
                      <Box
                        title={`${split.label}: ${currency.format(r.part)}`}
                        sx={{
                          height: "100%",
                          width: `${clamp((r.part / r.value) * 100, 0, 100)}%`,
                          bgcolor: split.color,
                        }}
                      />
                    )}
                    {split?.end &&
                      r.endPart !== undefined &&
                      r.endPart > 0 &&
                      r.value !== 0 && (
                        <Box
                          title={`${split.end.label}: ${currency.format(r.endPart)}`}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            height: "100%",
                            boxSizing: "border-box",
                            width: `${clamp((r.endPart / r.value) * 100, 0, 100)}%`,
                            bgcolor: theme.palette.background.paper,
                            border: `1px dashed ${split.end.color}`,
                          }}
                        />
                      )}
                  </Box>
                </Box>
                {zeroPct !== undefined && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -ZERO_LINE_OVERHANG_PX,
                      bottom: -ZERO_LINE_OVERHANG_PX,
                      left: `${zeroPct}%`,
                      borderLeft: `1px solid ${theme.palette.text.secondary}`,
                    }}
                  />
                )}
              </Box>
            </Box>
            {exact ? (
              amount
            ) : (
              <Tooltip title={currency.format(r.value)} placement="top" arrow>
                {amount}
              </Tooltip>
            )}
            {/* sub-1% must not round to 0% — that reads as an empty row. A row
                that is no part of the whole has no share to show. */}
            <Typography
              variant="caption"
              align="right"
              sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
            >
              {share !== undefined &&
                `${share !== 0 && share < 1 ? "<1" : Math.round(share)}%`}
            </Typography>
          </Box>
        );
      })}

      <Box
        sx={{
          ...grid,
          pt: 1.25,
          mt: 0.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          ...inLine({ ...lineGrid, mt: 0 }),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            // the amount stands in the column of the amounts above it
            ...inLine({ gridColumn: "span 2" }),
          }}
        >
          {t("general.dataAtoms.total")}
        </Typography>
        <Typography
          variant="body2"
          align="right"
          noWrap
          sx={{ ...TABULAR_NUMERALS, fontWeight: 700 }}
        >
          {format(total)}
        </Typography>
        <Box />
      </Box>
    </Box>
  );
};
